import { useMemo } from 'react';
import { Question, SubQuestion } from 'src/types/helios/Question';
import { getQuestionContent } from 'src/helper/questionHelper';
import {
  replaceMediaIdsWithPresignedUrlsForQuestion,
  replaceMediaIdsWithPresignedUrlsInSubQuestions,
} from 'src/utils/mediaUtils';
import { TranslationFormData } from 'src/validations/translationFormSchema';

/**
 * Custom hook to prepare translation form data with media URLs replaced
 * Handles the complex logic of replacing media IDs with presigned URLs
 * for both main question fields and subquestions
 *
 * @param question - The question being translated
 * @param selectedLanguage - The language to get content for
 * @returns Form data ready for React Hook Form initialization
 *
 * @example
 * ```typescript
 * const formData = useTranslationFormData(question, 'es');
 * const form = useForm({ defaultValues: formData });
 * ```
 */
export const useTranslationFormData = (
  question: Question,
  selectedLanguage: string,
): TranslationFormData => {
  return useMemo(() => {
    // Get the localized content for the selected language
    const content = getQuestionContent(question, selectedLanguage);

    // Fields that may contain media IDs to replace
    const questionFields = [
      'questionText',
      'auditInstruction',
      'passCriteria',
      'failCriteria',
      'justification',
      'taskDescription',
    ] as const;

    // Initialize form data with content
    const initialData: Record<string, any> = {
      title: content?.questionDetails?.title || '',
      questionText: content?.questionDetails?.questionText || '',
      auditInstruction: content?.questionDetails?.auditInstruction || '',
      passCriteria: content?.questionDetails?.passCriteria || '',
      failCriteria: content?.questionDetails?.failCriteria || '',
      justification: content?.questionDetails?.justification || '',
      taskName: content?.taskDetails?.taskName || '',
      taskDescription: content?.taskDetails?.taskDescription || '',
      // Flag to indicate if task section exists (for conditional validation)
      hasTask: !!(
        content?.taskDetails?.taskName?.trim() ||
        content?.taskDetails?.taskDescription?.trim()
      ),
      subQuestions: content?.subQuestions || [],
    };

    // Replace media IDs with presigned URLs for each field
    questionFields.forEach((field) => {
      if (initialData[field] && question.mediaResponses) {
        initialData[field] = replaceMediaIdsWithPresignedUrlsForQuestion(
          initialData[field],
          question.mediaResponses,
        );
      }
    });

    // Replace media IDs with presigned URLs in subQuestions for display
    if (initialData.subQuestions && question.subQuestions) {
      // Map translated subQuestions with original media responses for URL replacement
      const subQuestionsWithMediaResponses = initialData.subQuestions.map(
        (subQuestion: SubQuestion, index: number) => {
          const originalSubQuestion = question.subQuestions?.[index];
          if (!originalSubQuestion) return subQuestion;

          // Attach media responses from original question to translated content
          return {
            ...subQuestion,
            mediaResponses: originalSubQuestion.mediaResponses,
            mediaResponsesMap: originalSubQuestion.mediaResponsesMap,
          };
        },
      );

      // Use utility function to replace media IDs with presigned URLs
      initialData.subQuestions = replaceMediaIdsWithPresignedUrlsInSubQuestions(
        subQuestionsWithMediaResponses,
      );
    }

    return initialData as TranslationFormData;
  }, [question, selectedLanguage]);
};
